import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { deprecatedBreakpoints } from "@ui/hooks/useCurrentBreakpoint/useCurrentBreakpoint";
import classNames from "classnames";

import type { PictureSource } from "@jti/core-components/components/PictureBehaviour";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";

import { buildUri, sizes } from "./uriTemplate";

import styles from "./ResponsiveImage.module.scss";

export type ResponsiveImageProps = {
  alt?: string;
  title?: string;
  largeSrc: string;
  mediumSrc?: string;
  smallSrc?: string;
  onImageLoaded?: () => void;
  hideLoadingState?: boolean;
  className?: string;
  nativeBehaviour?: boolean;
};

export const ResponsiveImage: FC<ResponsiveImageProps> = ({
  alt = "",
  title = "",
  largeSrc,
  mediumSrc,
  smallSrc,
  onImageLoaded,
  hideLoadingState = false,
  className = "",
  nativeBehaviour = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageLoaded = useCallback(() => {
    setIsLoading(false);
    if (onImageLoaded) {
      onImageLoaded();
    }
  }, [onImageLoaded]);

  useEffect(() => {
    if (imgRef.current?.complete) {
      handleImageLoaded();
    }
  }, [handleImageLoaded, imgRef.current?.complete]);

  const sources: PictureSource[] = [];

  if (smallSrc) {
    sources.push({ srcSet: buildUri(smallSrc, sizes.small), media: deprecatedBreakpoints.small });
  }

  if (mediumSrc) {
    sources.push({ srcSet: buildUri(mediumSrc, sizes.medium), media: deprecatedBreakpoints.medium });
  }

  if (largeSrc) {
    sources.push({ srcSet: buildUri(largeSrc, sizes.large), media: deprecatedBreakpoints.large });
  }

  const imageProps = {
    className: "image",
    src: buildUri(largeSrc, sizes.large),
    alt,
    loading: "lazy" as const,
    onLoad: handleImageLoaded,
    title,
  };

  const containerClasses = classNames("imageWrapper", className, styles.responsiveImage, {
    [styles.loading]: isLoading && !hideLoadingState,
  });

  return (
    <div className={containerClasses}>
      <PictureBehaviour ref={imgRef} sources={sources} imageProps={imageProps} nativeBehaviour={nativeBehaviour} />
    </div>
  );
};
